import dayjs, { Dayjs } from 'dayjs';
import { DateTrigger } from 'src/components/DateTrigger';
import { SelectMenu, type SelectMenuOption } from 'src/components/SelectMenu';
import { RelativeDate } from './types';

const RELATIVE_DATES = [
  RelativeDate.TODAY,
  RelativeDate.TOMORROW,
  RelativeDate.ONE_WEEK_FROM_NOW,
  RelativeDate.YESTERDAY,
  RelativeDate.ONE_WEEK_AGO,
  RelativeDate.THIRTY_DAYS_AGO,
  RelativeDate.SIXTY_DAYS_AGO,
  RelativeDate.NINETY_DAYS_AGO,
  RelativeDate.CUSTOM,
];

const MENU_OPTIONS: SelectMenuOption[] = RELATIVE_DATES.map((option) => ({
  value: option,
  title: option,
}));

const fillCellSx = { width: '100%', minWidth: 0 };

function isRelativeDate(value: string): value is RelativeDate {
  return RELATIVE_DATES.includes(value as RelativeDate);
}

/**
 * A date condition's value: a list of dates relative to the run, where Custom Date turns the same
 * box into a calendar field. Clearing that field goes back to the list.
 */
export const RulesDatePicker = ({
  value,
  timezone,
  onChange,
}: {
  value: string | RelativeDate;
  timezone?: string;
  onChange: (value: { isRollingDate: boolean; value: RelativeDate | Dayjs }) => void;
}) => {
  if (value && !isRelativeDate(value)) {
    return (
      <DateTrigger
        label="Custom date"
        variant="field"
        value={dayjs(value)}
        timezone={timezone}
        placeholder="Value"
        onChange={(picked) => {
          if (picked) {
            onChange({ isRollingDate: false, value: picked });
          }
        }}
        onClear={() => onChange({ isRollingDate: true, value: RelativeDate.TODAY })}
        sx={fillCellSx}
      />
    );
  }

  return (
    <SelectMenu
      label="Date"
      variant="field"
      size="small"
      menuWidth="max-content"
      menuSx={{ minWidth: '12rem' }}
      options={MENU_OPTIONS}
      value={value || RelativeDate.TODAY}
      sx={fillCellSx}
      onChange={(picked) => {
        if (!isRelativeDate(picked)) {
          return;
        }
        if (picked === RelativeDate.CUSTOM) {
          onChange({ isRollingDate: false, value: dayjs() });
        } else {
          onChange({ isRollingDate: true, value: picked });
        }
      }}
    />
  );
};
